import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loginWithGoogle } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import {
  Upload, Sparkles, Wand2, ImagePlus, ChevronDown, Heart, RefreshCw, ArrowRight,
} from "lucide-react";

const STYLES = [
  { id: "modern", name: "Modern", desc: "Clean lines & calm" },
  { id: "cottage", name: "Cottage", desc: "Lush blooms & paths" },
  { id: "minimalist", name: "Minimalist", desc: "Serene zen" },
  { id: "tropical", name: "Tropical", desc: "Resort paradise" },
];

const Overline = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">{children}</span>
);

export default function Visualizer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [beforeImg, setBeforeImg] = useState(null);
  const [style, setStyle] = useState("modern");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // design doc
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setBeforeImg(e.target.result);
      setResult(null);
    };
    reader.readAsDataURL(file);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const generate = async () => {
    if (!user) {
      toast.info("Sign in to generate your design.");
      loginWithGoogle("/visualizer");
      return;
    }
    if (!beforeImg) {
      toast.error("Upload a photo of your space first.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/visualizer/generate", { image: beforeImg, style, notes });
      setResult(res.data);
      toast.success("Your design is ready!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const requestDesign = () => {
    if (!result) return;
    navigate(`/booking?service=land_design&design=${result.design_id}`);
  };

  const toggleFav = async () => {
    if (!result) return;
    try {
      const res = await api.patch(`/visualizer/designs/${result.design_id}/favorite`);
      setResult({ ...result, favorite: res.data.favorite });
      toast.success(res.data.favorite ? "Saved to favorites" : "Removed from favorites");
    } catch {
      toast.error("Could not update favorite.");
    }
  };

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <section className="bg-[#1A362D] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">AI Landscape Visualizer</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight max-w-3xl">
            See your dream yard in seconds
          </h1>
          <p className="mt-4 text-white/80 text-lg font-light max-w-2xl">
            Upload a photo, pick a style, and let our AI reimagine your outdoor space — then request that exact design.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* LEFT: Controls */}
          <div className="space-y-6">
            {/* Upload */}
            <div
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileRef.current?.click()}
              data-testid="visualizer-upload"
              className="relative cursor-pointer rounded-2xl border-2 border-dashed border-[#C7D2C8] bg-white p-8 text-center hover:border-[#8FA38C] transition-colors"
            >
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="visualizer-file-input" />
              {beforeImg ? (
                <div className="space-y-3">
                  <img src={beforeImg} alt="Your space" className="mx-auto max-h-56 rounded-xl object-contain" />
                  <p className="text-sm text-[#8FA38C] flex items-center justify-center gap-2"><ImagePlus className="h-4 w-4" /> Click to change photo</p>
                </div>
              ) : (
                <div className="py-8">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F2F4F2] text-[#1A362D] mx-auto">
                    <Upload className="h-6 w-6" />
                  </span>
                  <p className="mt-4 font-heading text-xl text-[#1A362D]">Upload a photo of your yard</p>
                  <p className="mt-1 text-sm text-[#8A9A8C]">Drag & drop or click to browse · JPG / PNG</p>
                </div>
              )}
            </div>

            {/* Style selection */}
            <div>
              <Overline>Choose a style</Overline>
              <div className="grid grid-cols-2 gap-3 mt-3">
                {STYLES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setStyle(s.id)}
                    data-testid={`style-${s.id}`}
                    className={`text-left p-4 rounded-xl border transition-all ${
                      style === s.id
                        ? "border-[#C47B57] bg-[#C47B57]/5 ring-1 ring-[#C47B57]"
                        : "border-[#E2E8E4] bg-white hover:border-[#8FA38C]"
                    }`}
                  >
                    <p className="font-medium text-[#1A362D]">{s.name}</p>
                    <p className="text-xs text-[#8A9A8C] mt-0.5">{s.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Advanced (progressive disclosure) */}
            <Collapsible>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 text-sm font-medium text-[#1A362D]" data-testid="advanced-toggle">
                  <ChevronDown className="h-4 w-4" /> Advanced — add specific requests
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3">
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="visualizer-notes"
                  placeholder="e.g. add a stone fire pit, low-maintenance plants, string lighting…"
                  rows={3}
                  className="bg-white border-[#E2E8E4]"
                />
              </CollapsibleContent>
            </Collapsible>

            <Button
              onClick={generate}
              disabled={loading}
              data-testid="visualizer-generate-btn"
              className="w-full bg-[#1A362D] hover:bg-[#254A3E] text-white rounded-full py-6 text-base"
            >
              {loading ? <><RefreshCw className="mr-2 h-5 w-5 animate-spin" /> Designing your space…</> : <><Wand2 className="mr-2 h-5 w-5" /> Generate Design</>}
            </Button>
            {!user && <p className="text-center text-xs text-[#8A9A8C]">You'll be asked to sign in before generating.</p>}
          </div>

          {/* RIGHT: Canvas */}
          <div className="rounded-2xl bg-white border border-[#E2E8E4]/60 p-6 flex flex-col" data-testid="visualizer-canvas">
            {loading ? (
              <div className="flex-1 flex flex-col items-center justify-center aspect-[4/3] bg-[#F2F4F2] rounded-xl">
                <Sparkles className="h-10 w-10 text-[#C47B57] animate-pulse" />
                <p className="mt-4 font-heading text-xl text-[#1A362D]">Crafting your transformation…</p>
                <p className="text-sm text-[#8A9A8C] mt-1">This usually takes 10–20 seconds</p>
              </div>
            ) : result ? (
              <div className="space-y-5">
                <BeforeAfterSlider before={result.before_image} after={result.after_image} />
                <div className="flex flex-wrap gap-3">
                  <Button onClick={requestDesign} data-testid="request-design-btn" className="flex-1 bg-[#C47B57] hover:bg-[#D58C68] text-white rounded-full">
                    Request This Design <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button onClick={toggleFav} variant="outline" data-testid="favorite-design-btn" className="rounded-full border-[#8FA38C]">
                    <Heart className={`h-4 w-4 ${result.favorite ? "fill-[#C47B57] text-[#C47B57]" : "text-[#1A362D]"}`} />
                  </Button>
                </div>
                <p className="text-xs text-[#8A9A8C] text-center">Saved to your portal · Style: {result.style}</p>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center aspect-[4/3] bg-[#F2F4F2] rounded-xl text-center px-6">
                <Sparkles className="h-10 w-10 text-[#8FA38C]" />
                <p className="mt-4 font-heading text-xl text-[#1A362D]">Your transformation appears here</p>
                <p className="text-sm text-[#8A9A8C] mt-1">Upload a photo and hit Generate to begin.</p>
              </div>
            )}
          </div>
        </div>

        {/* Gallery */}
        <div className="mt-20">
          <div className="text-center max-w-2xl mx-auto">
            <Overline>Inspiration</Overline>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold text-[#1A362D] tracking-tight">Recent transformations</h2>
            <p className="mt-3 text-[#4A5D4E] font-light">Drag the slider to reveal each before & after.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {gallery.map((g) => (
              <div key={g.id} className="space-y-3">
                <BeforeAfterSlider before={g.before} after={g.after} />
                <p className="text-center text-sm uppercase tracking-widest text-[#8FA38C]">{g.style} style</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
